'use client';

import { CSSProperties, ReactNode, useLayoutEffect, useRef } from 'react';

type HomepageViewportStyle = CSSProperties & {
    '--homepage-vh': string;
};

function getViewportHeight() {
    return window.visualViewport?.height ?? window.innerHeight;
}

export default function HomepageViewport({
    children,
}: {
    children: ReactNode;
}) {
    const rootRef = useRef<HTMLDivElement>(null);

    useLayoutEffect(() => {
        const root = rootRef.current;
        if (!root) return;

        let width = window.innerWidth;
        let height = getViewportHeight();

        const setViewportHeight = () => {
            root.style.setProperty('--homepage-vh', `${height}px`);
        };

        const handleResize = () => {
            const nextWidth = window.innerWidth;
            const nextHeight = getViewportHeight();
            const widthChanged = nextWidth !== width;
            const isTouchViewport = navigator.maxTouchPoints > 0;

            if (isTouchViewport && !widthChanged) {
                return;
            }

            width = nextWidth;
            height = nextHeight;
            setViewportHeight();
        };

        setViewportHeight();
        window.addEventListener('resize', handleResize);
        window.visualViewport?.addEventListener('resize', handleResize);
        window.addEventListener('orientationchange', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
            window.visualViewport?.removeEventListener('resize', handleResize);
            window.removeEventListener('orientationchange', handleResize);
        };
    }, []);

    return (
        <div
            ref={rootRef}
            className="contents"
            style={{ '--homepage-vh': '100svh' } as HomepageViewportStyle}
        >
            {children}
        </div>
    );
}
